import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import {
  buildAuditByReportId,
  hasPortfolioValidationFailure,
  summarizePortfolioAudit,
} from '../audit/portfolioAuditSummary';
import {
  buildPortfolioAdvisorInsights,
  buildPortfolioTaxInsight,
} from '../insights/buildPortfolioAdvisorInsights';
import {
  getReportClientLabel,
  getReportGoalLabel,
  getReportMarketValueCents,
  hasLiquidateTrigger,
  hasTransferTrigger,
} from '../presentation/reportLabels';

export type RunAttentionSortKey =
  | 'largestDeltaDollars'
  | 'highestTurnover'
  | 'taxableSells'
  | 'failed';

export type RunAttentionBadge =
  | 'failed'
  | 'liquidate'
  | 'transfer'
  | 'tax'
  | 'highTurnover';

export type RunAttentionPortfolioRow = {
  reportId: string;
  report?: PortfolioReport;
  audit?: OptimizerAuditV2Record;
  clientLabel: string;
  goalLabel: string;
  modelLabel: string;
  marketValueCents: number;
  totalBuyCents: number;
  totalSellCents: number;
  tradeCount: number;
  absDeltaCents: number;
  turnoverPct: number | null;
  taxableSellCents: number;
  passed: boolean;
  badges: RunAttentionBadge[];
};

export type BuildRunAttentionPortfolioRowsOptions = {
  sortKey?: RunAttentionSortKey;
  limit?: number;
  highTurnoverPct?: number | null;
};

const sumAbsDeltaCents = (
  audit: OptimizerAuditV2Record | undefined,
): number => {
  if (!audit) {
    return 0;
  }

  let total = 0;

  for (const subAccount of audit.subAccountResults ?? []) {
    for (const diff of subAccount.weightDiffs ?? []) {
      total += Math.abs(diff.deltaCents ?? 0);
    }
  }

  return total;
};

const hasValidationFailure = hasPortfolioValidationFailure;

const compareRows = (
  a: RunAttentionPortfolioRow,
  b: RunAttentionPortfolioRow,
  sortKey: RunAttentionSortKey,
): number => {
  switch (sortKey) {
    case 'highestTurnover':
      return (b.turnoverPct ?? -1) - (a.turnoverPct ?? -1);
    case 'taxableSells':
      return b.taxableSellCents - a.taxableSellCents;
    case 'failed':
      return (
        Number(hasValidationFailureByRow(b)) -
        Number(hasValidationFailureByRow(a))
      );
    case 'largestDeltaDollars':
    default:
      return b.absDeltaCents - a.absDeltaCents;
  }
};

const hasValidationFailureByRow = (row: RunAttentionPortfolioRow): boolean =>
  !row.passed || row.badges.includes('failed');

const buildRowBadges = (
  audit: OptimizerAuditV2Record | undefined,
  report: PortfolioReport | undefined,
  turnoverPct: number | null,
  taxableSellCents: number,
  highTurnoverPct: number | null,
): RunAttentionBadge[] => {
  const badges: RunAttentionBadge[] = [];

  if (hasValidationFailure(audit)) {
    badges.push('failed');
  }

  if (hasLiquidateTrigger(report, audit)) {
    badges.push('liquidate');
  }

  if (hasTransferTrigger(report, audit)) {
    badges.push('transfer');
  }

  if (taxableSellCents > 0) {
    badges.push('tax');
  }

  if (
    turnoverPct != null &&
    highTurnoverPct != null &&
    turnoverPct >= highTurnoverPct
  ) {
    badges.push('highTurnover');
  }

  return badges;
};

const buildPortfolioLabel = (
  report: PortfolioReport | undefined,
  reportId: string,
): { clientLabel: string; goalLabel: string } => {
  if (!report) {
    return { clientLabel: reportId, goalLabel: '' };
  }

  const client = getReportClientLabel(report);
  const goal = getReportGoalLabel(report);

  if (client && goal) {
    return { clientLabel: client, goalLabel: goal };
  }

  return {
    clientLabel: client || goal || report.id,
    goalLabel: client && goal ? goal : '',
  };
};

export const buildRunAttentionPortfolioRows = (
  translateBackendString: (translatedObject: unknown) => string,
  audits: OptimizerAuditV2Record[],
  reportsById: Map<string, PortfolioReport>,
  options: BuildRunAttentionPortfolioRowsOptions = {},
): RunAttentionPortfolioRow[] => {
  const {
    sortKey = 'largestDeltaDollars',
    limit = 10,
    highTurnoverPct: highTurnoverPctOverride,
  } = options;

  const auditByReportId = buildAuditByReportId(audits);
  const turnoverValues = audits
    .map((audit) => buildPortfolioAdvisorInsights(audit)?.turnoverPct)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);

  const defaultHighTurnoverPct =
    turnoverValues.length > 0
      ? turnoverValues[
          Math.min(
            turnoverValues.length - 1,
            Math.ceil(turnoverValues.length * 0.9) - 1,
          )
        ]
      : null;
  const highTurnoverPct = highTurnoverPctOverride ?? defaultHighTurnoverPct;

  const rows = [...auditByReportId.entries()].map(([reportId, audit]) => {
    const report = reportsById.get(reportId);
    const labels = buildPortfolioLabel(report, reportId);
    const tradeSummary = summarizePortfolioAudit(audit);
    const portfolioInsights = buildPortfolioAdvisorInsights(audit);
    const taxInsight = buildPortfolioTaxInsight(audit);
    const taxableSellCents = taxInsight?.taxableSellCents ?? 0;
    const turnoverPct = portfolioInsights?.turnoverPct ?? null;

    return {
      reportId,
      report,
      audit,
      clientLabel: labels.clientLabel,
      goalLabel: labels.goalLabel,
      modelLabel: report
        ? translateBackendString(report.modelPortfolio?.translatedName)
        : '',
      marketValueCents: report ? getReportMarketValueCents(report) : 0,
      totalBuyCents: tradeSummary?.totalBuyCents ?? 0,
      totalSellCents: tradeSummary?.totalSellCents ?? 0,
      tradeCount: tradeSummary?.tradeCount ?? 0,
      absDeltaCents: sumAbsDeltaCents(audit),
      turnoverPct,
      taxableSellCents,
      passed: !hasValidationFailure(audit),
      badges: buildRowBadges(
        audit,
        report,
        turnoverPct,
        taxableSellCents,
        highTurnoverPct,
      ),
    };
  });

  return rows.sort((a, b) => compareRows(a, b, sortKey)).slice(0, limit);
};
